import { useContext, useEffect, useMemo, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Account, CardTabs, PartnerAccountStatus } from 'vopay';
import useStorage from '@/hooks/useStorage';
import { useModuleFeatureFlag } from '@/providers/FeatureFlag.provider';
import useFetchAgreementDetails from '@/hooks/useApi/useFetchAgreementDetails';
import useFetchSigningAuthorityInfo from '@/hooks/useApi/useFetchSigningAuthorityInfo';
import AccountContext from '../AccountContext';
import Billing from '../Billing/Billing';
import AgreementContainer from '@/components/Accounts/Agreement/AgreementContainer';
import SignersContainer from '@/components/Accounts/Signers/SignersContainer';
import ApplicationPendingState from '@/components/Accounts/Account/ApplicationPendingState';
import AccountDetailsTab from './AccountDetailsTab/AccountDetailsTab';
import PermissionsTab from './PermissionsTab';
import TransactionsTab from './TransactionsTab';
import ClientAccountsTab from './ClientAccountsTab';
import SubAccountsTab from './SubAccountsTab';
import UsersTab from './UsersTab';
import WebhooksTab from './WebhooksTab';
import styles from './AccountTabs.module.scss';
import * as accountPageRoute from '@/components/Entities/Accounts/accountPage.route';

type AccountTabsProps = {
  /** The fetched account behind the Details tab: undefined while loading, null when the request failed. */
  account: Account | null | undefined;
  isPending: boolean;
  /** The account's status, which names the pending banner (ID-13964 D6); undefined until known. */
  status: PartnerAccountStatus | undefined;
  /** Reloads the account after its contacts were saved on the Details tab. */
  onContactsUpdated: () => Promise<unknown>;
  onBoardingLink: string | null;
  hasValidOnboardingUrl: boolean;
  isOnboardingEmailAllowed: boolean;
  onCopy: () => void;
  onSendEmail: () => void;
};

export default function AccountTabs({
  account,
  isPending,
  status,
  onContactsUpdated,
  onBoardingLink,
  hasValidOnboardingUrl,
  isOnboardingEmailAllowed,
  onCopy,
  onSendEmail,
}: AccountTabsProps) {
  const { t } = useTranslation('translations', { keyPrefix: 'Accounts.Account.Tabs' });
  const { details, balances } = useContext(AccountContext);
  const { canAccessBillingTab, canUpdatePermissions } = useModuleFeatureFlag('accounts');
  const history = useHistory();
  const { accountId, tab } = useParams<{ accountId: string; tab?: string }>();

  // The tab is a path segment (/accounts/:accountId/:tab) so a reload, or a link from elsewhere, lands
  // on the same one — and so the embedded lists, which write their filters to the query string, can't
  // drop it. Changing tab drops the query string with it: what is in there belongs to the tab being
  // left (the lists' own filters), and carrying it over would apply one tab's search to the next.
  const setActiveTab = (nextTab: accountPageRoute.AccountPageTab) =>
    history.replace({ pathname: accountPageRoute.getPath({ accountID: accountId, tab: nextTab }) });

  const {
    signerCreation: { getSignerCreation, clearSignerCreation },
    accountToken: { storeAccountToken },
  } = useStorage();

  const storedSignerCreation = getSignerCreation();
  const accountToken = details[accountId].token;
  const [agreementDetails, fetchAgreementDetails] = useFetchAgreementDetails({ bearer: accountToken });

  const [signingAuthorityInfo, fetchSigningAuthorityInfo] = useFetchSigningAuthorityInfo({
    bearer: accountToken || '',
  });

  const [isAddSignerToastVisible, setIsAddSignerToastVisible] = useState(false);

  // The list fetch already knows the account's sub-accounts and both entitlement flags, so the tab
  // bar does not wait on the details request.
  const listedAccount = useMemo(() => balances.find((item) => item.AccountID === accountId), [balances, accountId]);

  // Sub-accounts stay reachable on an account whose entitlement was later switched off.
  const showSubAccounts = Boolean(listedAccount?.SubAccountsEnabled) || (listedAccount?.SubAccounts ?? []).length > 0;
  const showClientAccounts = listedAccount?.ClientAccountsEnabled ?? details[accountId].clientAccountsEnabled;

  // A URL naming a tab this account has no trigger for (Billing without the flag, Sub-Accounts or
  // Client Accounts without the entitlement) lands on Details rather than on content the bar never
  // offers.
  const activeTab = useMemo<accountPageRoute.AccountPageTab>(() => {
    if (!accountPageRoute.isTab(tab)) return 'details';
    if (tab === 'billing' && !canAccessBillingTab) return 'details';
    if (tab === 'permissions' && !canUpdatePermissions) return 'details';
    if (tab === 'subaccounts' && !showSubAccounts) return 'details';
    if (tab === 'clientAccounts' && !showClientAccounts) return 'details';

    return tab;
  }, [tab, canAccessBillingTab, canUpdatePermissions, showSubAccounts, showClientAccounts]);

  useEffect(() => {
    storeAccountToken(accountToken);
  }, [details, accountId, accountToken]);

  useEffect(() => {
    fetchAgreementDetails();
  }, []);

  useEffect(() => {
    fetchSigningAuthorityInfo();
  }, [accountId, accountToken]);

  useEffect(() => {
    if (storedSignerCreation === 'signerToast') {
      fetchSigningAuthorityInfo();
      setActiveTab('signers');

      setTimeout(() => {
        clearSignerCreation();
      }, 200);

      setIsAddSignerToastVisible(true);

      setTimeout(() => {
        setIsAddSignerToastVisible(false);
      }, 2000);
    } else if (storedSignerCreation === 'signerChange') {
      fetchSigningAuthorityInfo();
      setActiveTab('signers');

      setTimeout(() => {
        clearSignerCreation();
      }, 200);
    }
  }, [storedSignerCreation]);

  const pendingState = (
    <ApplicationPendingState
      status={status}
      onBoardingLink={onBoardingLink}
      hasValidOnboardingUrl={hasValidOnboardingUrl}
      isOnboardingEmailAllowed={isOnboardingEmailAllowed}
      onCopy={onCopy}
      onSendEmail={onSendEmail}
    />
  );

  return (
    <CardTabs.Root
      value={activeTab}
      onValueChange={(value) => accountPageRoute.isTab(value) && setActiveTab(value)}
      className={styles.tabsRoot}
    >
      <CardTabs.List>
        <CardTabs.Trigger value="details">{t('details', { defaultValue: 'Details' })}</CardTabs.Trigger>

        {canAccessBillingTab && (
          <CardTabs.Trigger value="billing">{t('billing', { defaultValue: 'Billing' })}</CardTabs.Trigger>
        )}

        {/* The partner route behind the tab requires SetAccountPermissionsEnabled, so without it
            there is nothing to show. */}
        {canUpdatePermissions && (
          <CardTabs.Trigger value="permissions">{t('permissions', { defaultValue: 'Permissions' })}</CardTabs.Trigger>
        )}

        <CardTabs.Trigger value="webhooks">{t('webhooks', { defaultValue: 'Webhooks' })}</CardTabs.Trigger>

        <CardTabs.Trigger value="users">
          {t('accountPortalUsers', { defaultValue: 'Account Portal Users' })}
        </CardTabs.Trigger>

        {showSubAccounts && (
          <CardTabs.Trigger value="subaccounts">{t('subaccounts', { defaultValue: 'Sub-Accounts' })}</CardTabs.Trigger>
        )}

        {showClientAccounts && (
          <CardTabs.Trigger value="clientAccounts">
            {t('clientAccounts', { defaultValue: 'Client Accounts' })}
          </CardTabs.Trigger>
        )}

        <CardTabs.Trigger value="transactions">{t('transactions', { defaultValue: 'Transactions' })}</CardTabs.Trigger>
      </CardTabs.List>

      <CardTabs.Content value="details">
        {isPending ? (
          pendingState
        ) : (
          <AccountDetailsTab account={account} accountToken={accountToken} onContactsUpdated={onContactsUpdated} />
        )}
      </CardTabs.Content>

      <CardTabs.Content value="billing">{isPending ? pendingState : <Billing />}</CardTabs.Content>

      <CardTabs.Content value="permissions">
        {isPending ? pendingState : <PermissionsTab key={accountId} />}
      </CardTabs.Content>

      <CardTabs.Content value="webhooks">{isPending ? pendingState : <WebhooksTab key={accountId} />}</CardTabs.Content>
      <CardTabs.Content value="users">{isPending ? pendingState : <UsersTab key={accountId} />}</CardTabs.Content>
      <CardTabs.Content value="subaccounts">{isPending ? pendingState : <SubAccountsTab />}</CardTabs.Content>
      <CardTabs.Content value="clientAccounts">{isPending ? pendingState : <ClientAccountsTab />}</CardTabs.Content>
      <CardTabs.Content value="transactions">{isPending ? pendingState : <TransactionsTab />}</CardTabs.Content>

      <CardTabs.Content value="agreement">
        {isPending ? (
          pendingState
        ) : (
          <AgreementContainer
            agreementDetails={agreementDetails}
            accountToken={accountToken}
            signingAuthorityInfo={signingAuthorityInfo}
            onClick={() => setActiveTab('signers')}
          />
        )}
      </CardTabs.Content>

      <CardTabs.Content value="signers">
        {isPending ? (
          pendingState
        ) : (
          <SignersContainer
            agreementDetails={agreementDetails}
            accountToken={accountToken}
            isAddSignerToastVisible={isAddSignerToastVisible}
            signingAuthorityInfo={signingAuthorityInfo}
            handleSignerLists={() => fetchSigningAuthorityInfo()}
          />
        )}
      </CardTabs.Content>
    </CardTabs.Root>
  );
}
